import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useGetAdvisorInvestorInviteHistoryQuery } from '@/store/usersApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Box,
  Button,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from '@/config/dayjs';
import React from 'react';
import { useLocalization } from '@/hooks/useLocalization';

interface InvitationHistoryProps {
  investorId: number;
  investorName: string;
  onBack: () => void;
  showBackButton?: boolean;
  /**
   * Render the component's own title. Keep it `true` for the inline flyout view
   * (Client Details flyout / ClientContent, `invitationHistoryVariant: 'inline'`),
   * which has no surrounding title bar. Set `false` from the Clients-page dialog
   * (`invitationHistoryVariant: 'dialog'`), whose `DialogTitle` already shows the
   * heading — otherwise the title renders twice.
   */
  showTitle?: boolean;
}

const InvitationHistory: React.FC<InvitationHistoryProps> = ({
  investorId,
  investorName,
  onBack,
  showBackButton = true,
  showTitle = true,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const authLoaded = useAuthIsLoaded();
  const { data, isLoading, isFetching } =
    useGetAdvisorInvestorInviteHistoryQuery(
      authLoaded ? investorId : skipToken,
    );

  if (!authLoaded || isLoading || isFetching) {
    return <LoadingSpinner />;
  }

  const records = data?.Records ?? [];
  const dateLabel = t('date', 'Date');
  const sentByLabel = t('sentBy', 'Sent By');
  const formatDate = (value: string) => dayjs(value).format('M/D/YYYY h:mm a');

  const renderRecords = () => {
    if (records.length === 0) {
      return (
        <Typography>
          {t('invitationHistoryEmpty', 'This user has not been invited yet.')}
        </Typography>
      );
    }

    // Rendered as a simple flex layout rather than a table: this is a small,
    // read-only list, so it avoids table semantics (and the MUI-X table
    // requirement) per the reviewer's request to wrap the columns.
    if (isMobile) {
      // Full-screen dialog on mobile — stack each invitation as a labelled card.
      return (
        <Stack spacing={1}>
          {records.map((rec, idx) => (
            <Box
              key={`${rec.InviteDateTime}_${String(idx)}`}
              sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography variant='body2'>
                <Box
                  component='span'
                  sx={{ fontWeight: 600 }}
                >
                  {dateLabel}:
                </Box>{' '}
                {formatDate(rec.InviteDateTime)}
              </Typography>
              <Typography variant='body2'>
                <Box
                  component='span'
                  sx={{ fontWeight: 600 }}
                >
                  {sentByLabel}:
                </Box>{' '}
                {rec.AdvisorName}
              </Typography>
            </Box>
          ))}
        </Stack>
      );
    }

    // Desktop — two aligned columns via flexbox.
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            py: 1,
            fontWeight: 600,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ flex: 1 }}>{dateLabel}</Box>
          <Box sx={{ flex: 1 }}>{sentByLabel}</Box>
        </Box>
        {records.map((rec, idx) => (
          <Box
            key={`${rec.InviteDateTime}_${String(idx)}`}
            sx={{
              display: 'flex',
              gap: 2,
              py: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ flex: 1 }}>{formatDate(rec.InviteDateTime)}</Box>
            <Box sx={{ flex: 1 }}>{rec.AdvisorName}</Box>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Stack
      spacing={2}
      sx={{ px: 1 }}
    >
      {showBackButton ? (
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
          >
            {t('back', 'Back')}
          </Button>
        </Box>
      ) : null}

      {showTitle ? (
        <Typography
          variant='h6'
          component='h2'
        >
          {t('invitationHistoryTitleFor', {
            defaultValue: 'Invitation History for {{name}}',
            name: investorName,
          })}
        </Typography>
      ) : null}

      {renderRecords()}
    </Stack>
  );
};

export default InvitationHistory;
